import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { auth, db } from '../../firebase/config';
import { doc, setDoc } from 'firebase/firestore';

const PartnerSignUpContainer = styled.div`
  min-height: 100vh;
  display: flex;
  background-color: #f5f7fa;
`;

const ContentContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  padding: 2rem;
`;

const FormContainer = styled.div`
  background-color: white;
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows.md};
  padding: 2.5rem;
  width: 100%;
  max-width: 500px;
`;

const ImageContainer = styled.div`
  flex: 1;
  background-image: url('https://images.unsplash.com/photo-1556911220-bff31c812dba?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80');
  background-size: cover;
  background-position: center;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: none;
  }
`;

const Title = styled.h1`
  font-size: 1.75rem;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.textPrimary};
`;

const Subtitle = styled.p`
  font-size: 1rem;
  margin-bottom: 2rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const InputLabel = styled.label`
  font-size: 0.9rem;
  font-weight: ${props => props.theme.typography.weights.medium};
  color: ${props => props.theme.colors.textPrimary};
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 0.95rem;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.1);
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 0.95rem;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.1);
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Checkbox = styled.input`
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const SubmitButton = styled.button`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  padding: 0.75rem;
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 1rem;
  font-weight: ${props => props.theme.typography.weights.medium};
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin-top: 0.5rem;
  
  &:hover {
    background-color: ${props => props.theme.colors.primaryDark};
  }
`;

const LoginLink = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textSecondary};
  margin-top: 1.5rem;
  text-align: center;
  
  a {
    color: ${props => props.theme.colors.primary};
    text-decoration: none;
    font-weight: ${props => props.theme.typography.weights.medium};
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ErrorMessage = styled.div`
  background-color: #fee2e2;
  color: #b91c1c;
  padding: 0.75rem;
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

const PartnerSignUp = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    specialization: '',
    yearsExperience: '',
    agreeTerms: false
  });
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agreeTerms) {
      return setError('You must agree to the terms and conditions');
    }
    
    try {
      setError('');
      setLoading(true);
      
      // Create partner account
      const result = await signup(
        formData.email,
        formData.password,
        `${formData.firstName} ${formData.lastName}`,
        'partner' // Set role as partner
      );

      // Additional partner info saved to Firestore
      const partnerData = {
        specialization: formData.specialization,
        yearsExperience: formData.yearsExperience,
        pendingApproval: true,
        createdAt: new Date()
      };
      
      // Get the current user that was just created
      if (result && result.user) {
        await setDoc(doc(db, 'partners', result.user.uid), partnerData, { merge: true });
      } else {
        throw new Error('Failed to create user account');
      }
      
      // Show success message instead of redirecting
      setSuccess(true);
    } catch (err) {
      setError('Failed to create partner account: ' + err.message);
      console.error('Partner signup error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Show success message if registration was successful
  if (success) {
    return (
      <PartnerSignUpContainer>
        <ContentContainer>
          <FormContainer>
            <Title>Registration Successful</Title>
            <Subtitle>Thank you for registering as a partner!</Subtitle>
            
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <p>Your application is pending approval by our administrators.</p>
              <p>You will receive an email notification once your account has been approved.</p>
              <p>This typically takes 1-2 business days.</p>
            </div>
            
            <SubmitButton as={Link} to="/auth/login">
              Return to Login
            </SubmitButton>
          </FormContainer>
        </ContentContainer>
        <ImageContainer />
      </PartnerSignUpContainer>
    );
  }

  return (
    <PartnerSignUpContainer>
      <ContentContainer>
        <FormContainer>
          <Title>Join as a Partner</Title>
          <Subtitle>Create your partner account to connect with clients seeking nutritional guidance.</Subtitle>
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
          
          <Form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <FormGroup style={{ flex: 1 }}>
                <InputLabel htmlFor="firstName">First Name</InputLabel>
                <Input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              <FormGroup style={{ flex: 1 }}>
                <InputLabel htmlFor="lastName">Last Name</InputLabel>
                <Input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
            </div>
            
            <FormGroup>
              <InputLabel htmlFor="email">Email Address</InputLabel>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <InputLabel htmlFor="password">Password</InputLabel>
              <Input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <InputLabel htmlFor="specialization">Specialization</InputLabel>
              <Select
                id="specialization"
                name="specialization"
                value={formData.specialization}
                onChange={handleChange}
                required
              >
                <option value="">Select your specialization</option>
                <option value="clinical">Clinical Nutrition</option>
                <option value="sports">Sports Nutrition</option>
                <option value="weight">Weight Management</option>
                <option value="pediatric">Pediatric Nutrition</option>
                <option value="plant-based">Plant-based Nutrition</option>
                <option value="other">Other</option>
              </Select>
            </FormGroup>
            
            <FormGroup>
              <InputLabel htmlFor="yearsExperience">Years of Experience</InputLabel>
              <Input
                type="number"
                id="yearsExperience"
                name="yearsExperience"
                min="0"
                max="50"
                value={formData.yearsExperience}
                onChange={handleChange}
                required
              />
            </FormGroup>
            
            <CheckboxGroup>
              <Checkbox
                type="checkbox"
                id="agreeTerms"
                name="agreeTerms"
                checked={formData.agreeTerms}
                onChange={handleChange}
                required
              />
              <CheckboxLabel htmlFor="agreeTerms">
                I agree to the <Link to="/terms-and-conditions">Terms and Conditions</Link> and <Link to="/privacy-policy">Privacy Policy</Link>
              </CheckboxLabel>
            </CheckboxGroup>
            
            <SubmitButton type="submit" disabled={loading}>
              {loading ? 'Creating Account...' : 'Create Partner Account'}
            </SubmitButton>
            
            <LoginLink>
              Already have an account? <Link to="/auth/login">Log In</Link>
            </LoginLink>
          </Form>
        </FormContainer>
      </ContentContainer>
      <ImageContainer />
    </PartnerSignUpContainer>
  );
};

export default PartnerSignUp; 